import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Settings2, Trash2 } from "lucide-react";

const OPS_GROUPS = [
  { key: "ops_item_types", label: "Item Types" },
  { key: "ops_item_categories", label: "Item Categories" },
  { key: "ops_vendor_types", label: "Vendor Types" },
  { key: "ops_manufacturing_models", label: "MFG Models" },
  { key: "ops_location_types", label: "Location Types" },
  { key: "ops_bom_types", label: "BOM Types" },
  { key: "ops_uom_list", label: "UOM List" },
  { key: "ops_storage_conditions", label: "Storage Conditions" },
  { key: "ops_brands", label: "Brands" },
  { key: "ops_contact_roles", label: "Contact Roles" },
];

const SYSTEM_VALUES = {
  ops_item_types: ["RM","PM","SFG-Pop","SFG-Box","SFG-Bag","FG-Carton","FG-Buyout","Consumable","Spare-Part"],
  ops_vendor_types: ["rm_supplier","pm_supplier","3p_hybrid_manufacturer","3p_buyout_manufacturer","service_vendor","spare_vendor","logistics_vendor","other"],
  ops_manufacturing_models: ["inhouse","hybrid","buyout","na"],
  ops_location_types: ["plant","warehouse","zone","bin","3p_virtual_warehouse"],
  ops_bom_types: ["inhouse_multi_level","hybrid_pm_only","buyout_none"],
  ops_uom_list: ["kg","g","ltr","ml","pcs","box","bag","carton","roll","sheet","meter","nos"],
  ops_storage_conditions: ["ambient","cold","frozen"],
  ops_item_categories: ["Cartons","Monoboxes","Adhesives","Flavours","Colours","Laminates","Cleaning Agents","Spare Parts","Inks","Labels","Tapes","Other"],
  ops_brands: ["Skippi"],
  ops_contact_roles: ["Owner","Accountant","Finance Head","Quality Person","Warehouse Incharge","Sales Person","Other"],
};

function isSystem(group, value) {
  return (SYSTEM_VALUES[group] || []).includes(value);
}

export default function OpsConfigPage() {
  const [activeGroup, setActiveGroup] = useState(OPS_GROUPS[0].key);
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  const refreshConfigs = async () => {
    const groups = OPS_GROUPS.map(g => g.key).join(",");
    const { data } = await api.get("/config/batch", { params: { groups } });
    setConfigs(data);
  };

  useEffect(() => {
    setLoading(true);
    refreshConfigs().then(() => setLoading(false)).catch(() => setLoading(false));
  }, []);

  const currentItems = (configs[activeGroup] || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    try {
      await api.post("/config", {
        config_group: activeGroup,
        config_key: newValue.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_"),
        config_value: newValue.trim(),
        display_order: currentItems.length,
        is_active: true,
        is_system: false,
        metadata: {},
      });
      toast.success("Config value added");
      setNewValue("");
      setShowAdd(false);
      await refreshConfigs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleEdit = async () => {
    if (!editItem || !editValue.trim()) return;
    try {
      await api.put("/ops/config/by-key", {
        config_group: activeGroup,
        config_key: editItem.key,
        config_value: editValue.trim(),
      });
      toast.success("Updated");
      setEditItem(null);
      await refreshConfigs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete("/ops/config/by-key", {
        data: { config_group: activeGroup, config_key: deleteItem.key },
      });
      toast.success("Config value deleted");
      setDeleteItem(null);
      await refreshConfigs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="ops-config-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-zinc-500" />
          <h1 className="text-2xl font-bold tracking-tight" data-testid="ops-config-title">OPS Configuration</h1>
        </div>
        <Button onClick={() => { setNewValue(""); setShowAdd(true); }} data-testid="add-config-btn"><Plus className="w-4 h-4 mr-1" />Add Value</Button>
      </div>

      <Tabs value={activeGroup} onValueChange={setActiveGroup}>
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="config-tabs">
          {OPS_GROUPS.map(g => (
            <TabsTrigger key={g.key} value={g.key} className="text-xs" data-testid={`tab-${g.key}`}>{g.label}</TabsTrigger>
          ))}
        </TabsList>

        {OPS_GROUPS.map(g => (
          <TabsContent key={g.key} value={g.key}>
            <div className="border rounded-lg overflow-auto" data-testid={`config-table-${g.key}`}>
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead className="w-36">Key</TableHead>
                    <TableHead className="w-20">System</TableHead>
                    <TableHead className="w-20">Active</TableHead>
                    <TableHead className="w-28 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-zinc-400">Loading...</TableCell></TableRow>
                  ) : currentItems.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-zinc-400">No values configured</TableCell></TableRow>
                  ) : currentItems.map((item, idx) => {
                    const sys = isSystem(g.key, item.value);
                    return (
                      <TableRow key={item.key || idx} data-testid={`config-row-${item.value}`}>
                        <TableCell className="text-xs text-zinc-400">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{item.value}</TableCell>
                        <TableCell className="font-mono text-xs text-zinc-500">{item.key}</TableCell>
                        <TableCell>{sys ? <Badge variant="outline" className="text-xs">System</Badge> : "—"}</TableCell>
                        <TableCell><Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge></TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="icon" variant="ghost" onClick={() => { setEditItem(item); setEditValue(item.value); }} data-testid={`edit-config-${item.value}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {!sys && (
                            <Button size="icon" variant="ghost" onClick={() => setDeleteItem(item)} data-testid={`delete-config-${item.value}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} data-testid="add-config-dialog">
          <DialogHeader><DialogTitle>Add Config Value to "{OPS_GROUPS.find(g => g.key === activeGroup)?.label}"</DialogTitle></DialogHeader>
          <Label>Value</Label>
          <Input value={newValue} onChange={e => setNewValue(e.target.value)} placeholder="Enter new value" data-testid="new-config-value-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="save-config-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} data-testid="edit-config-dialog">
          <DialogHeader><DialogTitle>Edit Config Value</DialogTitle></DialogHeader>
          <Label>Current key: <span className="font-mono text-zinc-500">{editItem?.key}</span></Label>
          <Label className="mt-2">New Display Value</Label>
          <Input value={editValue} onChange={e => setEditValue(e.target.value)} data-testid="edit-config-value-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={handleEdit} data-testid="update-config-btn">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} data-testid="delete-config-dialog">
          <DialogHeader><DialogTitle>Delete Config Value</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">
            Are you sure you want to delete <strong>{deleteItem?.value}</strong> from {OPS_GROUPS.find(g => g.key === activeGroup)?.label}?
            This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItem(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-config-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
